import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Loader2, Send } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/shared'
import { emailTemplateService } from '@/services/emailTemplateService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'
import { useAuthStore } from '@/store/authStore'
import EmailEditor from '@/components/MarketingEmail/EmailEditor'
import EmailPreview from '@/components/MarketingEmail/EmailPreview'

const TemplateEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const isEditMode = !!id

  const templateId = id || null
  const [name, setName] = useState('')
  const [subjectLine, setSubjectLine] = useState('')
  const [previewText, setPreviewText] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [saving, setSaving] = useState(false)
  const [testSendOpen, setTestSendOpen] = useState(false)
  const [testSendEmail, setTestSendEmail] = useState(user?.email || '')
  const [testSending, setTestSending] = useState(false)

  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)
  const customFieldDefinitions = fieldsData?.data?.fields || []

  const { data: templateData, isLoading } = useQuery(
    ['email-template', id],
    () => emailTemplateService.getTemplateById(id),
    { enabled: isEditMode }
  )

  useEffect(() => {
    const template = templateData?.data?.template
    if (template) {
      setName(template.name)
      setSubjectLine(template.subject_line || '')
      setPreviewText(template.preview_text || '')
      setHtmlBody(template.html_body || '')
    }
  }, [templateData])

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required')
      return
    }
    setSaving(true)
    try {
      const payload = { name, subject_line: subjectLine, preview_text: previewText, html_body: htmlBody }
      if (templateId) {
        await emailTemplateService.updateTemplate(templateId, payload)
        toast.success('Template saved')
      } else {
        await emailTemplateService.createTemplate(payload)
        toast.success('Template created')
      }
      queryClient.invalidateQueries('email-templates')
      navigate('/marketing-email/templates')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = useCallback(async (file) => {
    if (!templateId) {
      toast.error('Save the template first to upload images')
      return null
    }
    try {
      const res = await emailTemplateService.uploadImage(templateId, file)
      return res.data.url
    } catch (error) {
      toast.error(error.response?.data?.error || 'Image upload failed')
      return null
    }
  }, [templateId])

  const handleTestSend = async () => {
    if (!templateId) return
    setTestSending(true)
    try {
      await emailTemplateService.testSend(templateId, testSendEmail)
      toast.success(`Test email sent to ${testSendEmail}`)
      setTestSendOpen(false)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send test email')
    } finally {
      setTestSending(false)
    }
  }

  if (isEditMode && isLoading) {
    return <div className="text-center py-12 text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditMode ? 'Edit Template' : 'New Template'}
        description="Design a reusable email template"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/templates')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {templateId && (
              <Button variant="outline" size="sm" onClick={() => setTestSendOpen(true)}>
                <Send className="mr-2 h-4 w-4" />
                Test Send
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Template Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monthly Newsletter" />
              </div>
              <div className="space-y-1.5">
                <Label>Subject Line</Label>
                <Input value={subjectLine} onChange={(e) => setSubjectLine(e.target.value)} placeholder="e.g. Your {{month}} update is here" />
              </div>
              <div className="space-y-1.5">
                <Label>Preview Text</Label>
                <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="Shown next to the subject line in most inboxes" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Email Body</CardTitle>
            </CardHeader>
            <CardContent>
              <EmailEditor
                content={htmlBody}
                onChange={setHtmlBody}
                onImageUpload={handleImageUpload}
                customFieldDefinitions={customFieldDefinitions}
                placeholder="Write your email content here..."
              />
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Live Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <EmailPreview subject={subjectLine} previewText={previewText} html={htmlBody} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={testSendOpen} onOpenChange={setTestSendOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>Send yourself a real copy of this template to check formatting.</DialogDescription>
          </DialogHeader>
          <Input value={testSendEmail} onChange={(e) => setTestSendEmail(e.target.value)} placeholder="you@example.com" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestSendOpen(false)}>Cancel</Button>
            <Button onClick={handleTestSend} disabled={testSending}>
              {testSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default TemplateEditor
